import { useEffect, useRef } from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

const POPOVER_SELECTOR = `[data-palette-overlay="true"],[data-spine-overlay="true"],[data-popover-overlay="true"]`;
const TOAST_SELECTOR = `[data-sonner-toast]`;
const ANY_SELECTOR = [POPOVER_SELECTOR, TOAST_SELECTOR].join(',');

/**
 * Get the Reflag dialog element.
 */
function getReflagDialogElement(): HTMLDialogElement | null {
    return (
        document
            .getElementById('reflag-feedback-dialog-container')
            ?.shadowRoot?.querySelector('dialog') ?? null
    );
}

/**
 * Check if an element is part of an overlay.
 */
export function checkIsElementContainsInOverlay(element: Element) {
    if (element.closest(ANY_SELECTOR)) {
        return true;
    }

    if (getReflagDialogElement()?.contains(element)) {
        return true;
    }

    return false;
}

/**
 * Check if a popover is open.
 */
function checkIsPopoverOpen() {
    if (document.querySelector(POPOVER_SELECTOR)) {
        return true;
    }
    if (getReflagDialogElement()?.open) {
        return true;
    }
    return false;
}

/**
 * Check if a popover is open and allows to pass a `onClose`
 * callback to be notified when it's closed.
 */
export function useCheckIsPopoverOpen() {
    const mouseUpCleanupRef = useRef<(() => void) | null>(null);
    useEffect(() => () => mouseUpCleanupRef.current?.(), []);
    return useEventCallback((options?: { onClose?: () => void }) => {
        const onClose = options?.onClose;
        if (checkIsPopoverOpen()) {
            mouseUpCleanupRef.current?.();

            // When a mouse up event is triggered, we reevaluate if the popover is open.
            const handleMouseUp = () => {
                // Popover is closed, we cleanup the event listener.
                if (!checkIsPopoverOpen()) {
                    mouseUpCleanupRef.current?.();
                    onClose?.();
                }
            };
            document.body.addEventListener('mouseup', handleMouseUp);
            mouseUpCleanupRef.current = () => {
                document.body.removeEventListener('mouseup', handleMouseUp);
                mouseUpCleanupRef.current = null;
            };
            return true;
        }
        return false;
    });
}
